/* ═══════════════════════════════════════════════════════
   suite-io — Docs & Files Tab (Job Detail)
   ═══════════════════════════════════════════════════════
   Ported from legacy nyc-suite DocsFilesTab.jsx (cabinet mode).

   Carried over: generated-docs list (shared-with-client toggle,
   signature-status display, view, delete), conversation transcripts
   with AI Extract & Populate + Who's Who speaker linking, and the
   per-job file cabinet (Storage upload, crew-visible toggle,
   download, delete) on the shared files/{jobId} collection.

   Deliberately NOT yet ported (with the doc-builder / DocuSign
   passes): Build Doc mode (template picker + Lil Buddy split view +
   Revise), "Upload Returned PDF" form ingestion, and DocuSign
   send-for-signature / check-status actions. Signature status from
   legacy-sent docs still displays.

   Adaptations from legacy:
   - Who's Who links write job.linkedContacts ({contactId, role}) —
     the CRM-v2 field LinkedContacts.jsx prefers — not the legacy
     linkedEntities.
   - Studio autofill uses studioPatch() instead of hardcoded address
     lines.
   - equipmentIdToName derives from DataContext studioSettings.
*/

import { useState, useCallback, useEffect, useMemo } from "react";
import DOMPurify from "dompurify";
import { C, font, inp, lbl, sel, btnS, btnP, btnD, badge } from "../../../../lib/styles.js";
import { fl, BLANK_SESSION, BLANK_CONTACT, studioPatch, fmtDate, FMT_DATE_FULL, seededLinkedContacts } from "../../../../lib/utils.js";
import { INDUSTRY_LABELS, JOB_ROLES } from "../../../../lib/constants.js";
import { useData } from "../../../../context/DataContext.jsx";
import { useDoc } from "../../../../context/DocContext.jsx";
import { dataService } from "../../../../lib/dataService.js";
import { storage, storageRef, uploadBytes, getDownloadURL } from "../../../../firebase.js";
import { extractJobFromConversation } from "../../../../lib/ai.js";
import CrewCardSection from "./CrewCardSection.jsx";
import ClientCardSection from "./ClientCardSection.jsx";

function forceDownload(url, fileName) {
  const a = document.createElement("a");
  a.href = url; a.download = fileName || "download";
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
}

// Keep Storage object names filesystem/URL-safe and bounded (mirrors
// attachmentStorage's safeName; the original name stays on the metadata
// and is what the download is saved as).
const safeName = (name) =>
  (name || "file").replace(/[^a-zA-Z0-9._-]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 120) || "file";

// Inline amber confirm strip — the app's confirm style (matches the
// un-invoice confirm on Financials). No window.confirm anywhere.
function ConfirmStrip({ label, onYes, onNo, yesLabel = "Yes, delete" }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.2)", borderRadius: 6, margin: "4px 0 6px" }}>
      <span style={{ color: C.amber, fontSize: 13, flexShrink: 0 }}>{"⚠"}</span>
      <span style={{ fontSize: 11, color: C.text, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
      <button style={{ ...btnS, padding: "4px 10px", fontSize: 10, color: C.amber, borderColor: C.amber + "50" }} onClick={onYes}>{yesLabel}</button>
      <button style={{ ...btnS, padding: "4px 10px", fontSize: 10 }} onClick={onNo}>Cancel</button>
    </div>
  );
}

/* One file row — shared by the admin cabinet (crew toggle + delete) and the
   crew Files tab (read-only: just name, date, size, download). */
export function FileRow({ file: f, editing = false, onToggleCrew, onDelete }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const ext = (f.extension || f.fileName?.split(".").pop() || "").toLowerCase();
  const icon = ext === "pdf" ? "📄" : ext === "docx" || ext === "doc" ? "📝" : ext === "xlsx" || ext === "xls" ? "📊" : ["png", "jpg", "jpeg"].includes(ext) ? "🖼" : "📎";
  const size = f.size ? (f.size > 1024 * 1024 ? `${(f.size / 1024 / 1024).toFixed(1)} MB` : `${Math.round(f.size / 1024)} KB`) : "";
  return (
    <div style={{ marginBottom: 2 }}>
      <div className="jdf-filerow" style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 10px", borderRadius: 4, flex: 1, minWidth: 0, background: `${C.border}15`, border: `1px solid ${C.border}22` }}>
          <span style={{ fontSize: 13 }}>{icon}</span>
          <span style={{ flex: 1, fontSize: 11, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.fileName}</span>
          {/* uploadedAt is full ISO; fmtDate parses YYYY-MM-DD only — slice to the date part */}
          {f.uploadedAt && <span style={{ fontSize: 9, color: C.muted, whiteSpace: "nowrap" }}>{fmtDate(String(f.uploadedAt).slice(0, 10), FMT_DATE_FULL)}</span>}
          <span style={{ fontSize: 10, color: C.muted, whiteSpace: "nowrap" }}>{size}</span>
          <button onClick={(e) => { e.preventDefault(); forceDownload(f.downloadUrl, f.fileName); }} style={{ ...btnS, padding: "3px 8px", fontSize: 9, display: "flex", alignItems: "center", gap: 3, cursor: "pointer" }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
          </button>
        </div>
        {editing && onToggleCrew && <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 9, color: f.crewVisible ? C.green : C.muted, cursor: "pointer", whiteSpace: "nowrap" }}>
          <input type="checkbox" checked={f.crewVisible || false} onChange={onToggleCrew} style={{ accentColor: C.green }} />
          Crew
        </label>}
        {editing && onDelete && <button style={{ ...btnD, padding: "3px 6px", fontSize: 9 }} onClick={() => setConfirmDelete(true)}>×</button>}
      </div>
      {confirmDelete && (
        <ConfirmStrip label={`Delete ${f.fileName}?`} onYes={() => { setConfirmDelete(false); onDelete(); }} onNo={() => setConfirmDelete(false)} />
      )}
    </div>
  );
}

const SIG_STATUS_COLOR = {
  sent: C.blue,
  delivered: C.amber,
  viewed: C.amber,
  completed: C.green,
  declined: C.danger,
  voided: C.muted,
};

export default function DocsFilesTab({ job, editing, updateJobFields, user, initialPane = "cards" }) {
  // Docs & Files has four functions; one pane at a time keeps each in its lane.
  const [pane, setPane] = useState(initialPane);
  const { contacts, saveContact, studioSettings } = useData();
  const { savedDocs, saveDoc, deleteDoc } = useDoc();

  // Equipment id → display name (AI returns ids; the job stores names).
  // studioSettings.equipment is { Category: [{id, name, note}, ...], ... }.
  const equipmentIdToName = useMemo(() => {
    const map = {};
    Object.values(studioSettings?.equipment || {}).forEach(items => {
      (items || []).forEach(it => { if (it?.id && it?.name) map[it.id] = it.name; });
    });
    return map;
  }, [studioSettings?.equipment]);

  // Conversation transcript state
  const [convoMode, setConvoMode] = useState(false);
  const [convoText, setConvoText] = useState("");
  const [convoTitle, setConvoTitle] = useState("");
  // Extract & Populate state
  const [convoExtracting, setConvoExtracting] = useState(false);
  const [convoResult, setConvoResult] = useState(null);
  const [convoSkipped, setConvoSkipped] = useState(new Set());
  const [convoParticipants, setConvoParticipants] = useState([]);

  // File upload state — jobFiles lives here (loaded per job below); legacy kept
  // it in JobsPanel because the delivery tab shared it, which suite-io doesn't.
  const [jobFiles, setJobFiles] = useState([]);
  const [filesUploading, setFilesUploading] = useState(false);
  const [filesDragOver, setFilesDragOver] = useState(false);

  // Expanded conversation + doc viewer overlay
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [viewDoc, setViewDoc] = useState(null);

  // Inline errors (no native alert/confirm popups)
  const [filesError, setFilesError] = useState(null);
  const [convoError, setConvoError] = useState(null);
  const [wwError, setWwError] = useState(null);

  // Extract run on an already-saved conversation: points at the source doc so
  // Apply updates its participants in place instead of saving a duplicate.
  const [convoSourceDocId, setConvoSourceDocId] = useState(null);
  const [extractingDocId, setExtractingDocId] = useState(null);

  // Rename a saved conversation (edit mode)
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal] = useState("");

  // Inline delete confirms for generated docs + conversations
  const [confirmDeleteDocId, setConfirmDeleteDocId] = useState(null);
  const [confirmDeleteConvoId, setConfirmDeleteConvoId] = useState(null);

  // Defensive aliases
  const j = job || {};
  const jobDocs = j.jobDocs || [];
  const files = jobFiles || [];

  // Load job files — migrates any legacy-keyed files on first open.
  useEffect(() => {
    if (!j.id) { setJobFiles([]); return; }
    let cancelled = false;
    (async () => {
      const merged = await dataService.files.loadAndMigrate(j.id, j.jobId);
      if (!cancelled) setJobFiles(merged);
    })();
    return () => { cancelled = true; };
  }, [j.id]);

  // Generated docs for this job from the docs collection
  const generatedDocs = useMemo(() => {
    return (savedDocs || []).filter(d => d.jobId === j.id).sort((a, b) => (b.updatedAt || b.createdAt || "").localeCompare(a.updatedAt || a.createdAt || ""));
  }, [savedDocs, j.id]);

  const upd = useCallback((field, value) => {
    updateJobFields?.({ [field]: value });
  }, [updateJobFields]);

  const toggleShared = useCallback((docId) => {
    const doc = (savedDocs || []).find(d => d.id === docId);
    if (doc) saveDoc({ ...doc, sharedWithClient: !doc.sharedWithClient });
  }, [savedDocs, saveDoc]);

  // ═══ FILE UPLOAD ═══

  const handleUpload = useCallback(async (fileList) => {
    if (!fileList.length || filesUploading || !j.id) return;
    setFilesUploading(true);
    setFilesError(null);
    try {
      const ts = Date.now();
      const newEntries = await Promise.all(fileList.map(async (file, idx) => {
        const path = `job-files/${j.id}/${ts}-${idx}-${safeName(file.name)}`;
        const ref = storageRef(storage, path);
        await uploadBytes(ref, file);
        const url = await getDownloadURL(ref);
        return {
          fileName: file.name,
          downloadUrl: url,
          extension: file.name.split(".").pop().toLowerCase(),
          size: file.size,
          crewVisible: false,
          uploadedAt: new Date().toISOString(),
        };
      }));
      // Use functional updater to avoid stale closure over jobFiles
      let updated, prev;
      setJobFiles(p => { prev = p; updated = [...p, ...newEntries]; return updated; });
      try {
        await dataService.files.save(j.id, updated);
      } catch (saveErr) {
        console.error("File save:", saveErr);
        setJobFiles(prev);
        setFilesError("Upload saved locally but failed to sync: " + saveErr.message);
      }
    } catch (err) {
      console.error("File upload:", err);
      setFilesError("Upload failed: " + err.message);
    }
    setFilesUploading(false);
  }, [filesUploading, j.id]);

  // ═══ AI EXTRACT ═══
  // One extract path for both entries: the paste-a-transcript input box and
  // the Extract button on an already-saved conversation (sourceDocId set →
  // Apply updates that doc's participants in place instead of re-saving it).
  const runExtract = async (text, { sourceDocId = null, title = "" } = {}) => {
    if (!text?.trim() || convoExtracting) return;
    setConvoExtracting(true);
    setExtractingDocId(sourceDocId);
    setConvoError(null);
    setWwError(null);
    try {
      const result = await extractJobFromConversation(text);
      // Parse speaker labels from diarized transcript + use AI speakerMap
      const speakerMap = result.speakerMap || {};
      const myName = (user?.name || "").toLowerCase().trim();
      const speakerSnippets = {};
      const speakerRegex = /\[(\w+(?:_\d+)?)\s*[—–-]\s*[\d:]+\]\n(.+)/g;
      let match;
      while ((match = speakerRegex.exec(text)) !== null) {
        const label = match[1];
        if (label === "Unknown") continue;
        if (!speakerSnippets[label]) {
          speakerSnippets[label] = match[2].trim().slice(0, 80) + (match[2].length > 80 ? "…" : "");
        }
      }
      const speakerLabels = Object.keys(speakerSnippets);
      const ents = contacts || [];
      const participants = speakerLabels.map(label => {
        const guessedName = speakerMap[label] || "";
        const guessLower = guessedName.toLowerCase().trim();
        const isMe = myName && myName.length >= 3 && guessLower && (guessLower.includes(myName) || myName.includes(guessLower));
        const entMatch = guessedName ? ents.find(e => e.name && e.name.toLowerCase().trim() === guessLower) : null;
        return { label, snippet: speakerSnippets[label], name: guessedName, search: guessedName, contactId: entMatch?.id || null, role: isMe ? "other" : "contact", isMe, showDropdown: false };
      });
      if (sourceDocId) {
        setConvoText(text);
        setConvoTitle(title);
      }
      setConvoSourceDocId(sourceDocId);
      setConvoSkipped(new Set());
      setConvoParticipants(participants);
      setConvoResult(result);
    } catch (err) {
      setConvoError("Extraction failed: " + err.message);
    }
    setConvoExtracting(false);
    setExtractingDocId(null);
  };

  const resetConvoFlow = () => {
    setConvoResult(null); setConvoMode(false); setConvoText(""); setConvoTitle("");
    setConvoSkipped(new Set()); setConvoParticipants([]); setConvoSourceDocId(null);
    setConvoError(null); setWwError(null);
  };

  return (
    <div style={{ fontFamily: font }}>
      {/* Pane switcher: Sheets | Documents | Conversations | Files */}
      <div className="jdf-panes" style={{ display: "flex", gap: 2, borderBottom: `1px solid ${C.border}`, marginBottom: 14, flexWrap: "wrap" }}>
        {[
          ["cards", "Sheets"],
          ["documents", `Documents${generatedDocs.length ? ` (${generatedDocs.length})` : ""}`],
          ["conversations", `Conversations${jobDocs.length ? ` (${jobDocs.length})` : ""}`],
          ["files", `Files${files.length ? ` (${files.length})` : ""}`],
        ].map(([key, label]) => (
          <button key={key} type="button" onClick={() => setPane(key)} style={{
            fontFamily: font, fontSize: 12, padding: "8px 12px", border: "none", background: "transparent", cursor: "pointer",
            color: pane === key ? C.text : C.muted, borderBottom: pane === key ? `2px solid ${C.text}` : "2px solid transparent", marginBottom: -1,
          }}>{label}</button>
        ))}
      </div>

      {pane === "cards" && (
        <div style={{ display: "grid", gap: 16 }}>
          {/* Call sheet (crew) and session sheet (client): the day-of sheets built from this job (docs/crew-cards/DESIGN.md) */}
          <CrewCardSection key={j.id} job={j} updateJobFields={updateJobFields} user={user} />
          <ClientCardSection key={j.id} job={j} updateJobFields={updateJobFields} editing={editing} />
        </div>
      )}

      {pane === "documents" && (<>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ ...lbl, margin: 0 }}>Documents</div>
      </div>

      {/* Generated docs from docs collection */}
      {generatedDocs.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          {generatedDocs.map(doc => {
            const sigColor = SIG_STATUS_COLOR[doc.signatureStatus] || null;
            return (
              <div key={doc.id} style={{ borderBottom: `1px solid ${C.border}22` }}>
                <div className="jdf-docrow" style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", flexWrap: "wrap" }}>
                <span style={{ fontSize: 14 }}>📄</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.title || "Untitled"}</span>
                    {doc.signatureStatus && (
                      <span style={{ ...badge(sigColor), fontSize: 8, flexShrink: 0 }}>{doc.signatureStatus}</span>
                    )}
                  </div>
                  <div style={{ fontSize: 10, color: C.muted }}>
                    {(doc.updatedAt || doc.createdAt || "").slice(0, 10)}
                    {doc.category && doc.category !== "general" ? ` · ${doc.category.replace(/_/g, " ")}` : ""}
                    {doc.signerEmail && doc.signatureStatus ? ` · ${doc.signerEmail}` : ""}
                  </div>
                </div>
                <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: doc.sharedWithClient ? C.green : C.muted, cursor: "pointer", flexShrink: 0 }}>
                  <input type="checkbox" checked={doc.sharedWithClient || false} onChange={() => toggleShared(doc.id)} style={{ accentColor: C.green }} />
                  Shared
                </label>
                <button style={{ ...btnS, padding: "3px 8px", fontSize: 9 }} onClick={() => setViewDoc(doc)}>View</button>
                {editing && <button style={{ ...btnD, padding: "3px 8px", fontSize: 9 }} onClick={() => setConfirmDeleteDocId(doc.id)}>Delete</button>}
                </div>
                {confirmDeleteDocId === doc.id && (
                  <ConfirmStrip
                    label={`Delete "${doc.title || "Untitled"}"?`}
                    onYes={() => { setConfirmDeleteDocId(null); deleteDoc(doc.id); }}
                    onNo={() => setConfirmDeleteDocId(null)}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {generatedDocs.length === 0 && (
        <div style={{ padding: 20, textAlign: "center", color: C.muted, fontSize: 12, marginBottom: 14 }}>
          No documents generated yet.
        </div>
      )}

      {/* Read-only doc viewer overlay (Build/Revise come with the doc builder) */}
      {viewDoc && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(40,40,42,0.92)", display: "flex", flexDirection: "column", fontFamily: font }} onClick={() => setViewDoc(null)}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 22px", borderBottom: "1px solid rgba(255,255,255,0.12)" }} onClick={e => e.stopPropagation()}>
            <span style={{ color: "#ddd", fontSize: 13, letterSpacing: "0.03em" }}>{viewDoc.title || "Untitled"}</span>
            <button type="button" style={btnS} onClick={() => setViewDoc(null)}>← Back</button>
          </div>
          <div style={{ flex: 1, overflow: "auto", padding: "28px 16px", display: "flex", justifyContent: "center" }} onClick={e => e.stopPropagation()}>
            <div
              style={{ background: "#fff", color: "#1a1a1a", maxWidth: 720, width: "100%", padding: "48px 56px", borderRadius: 4, boxShadow: "0 8px 60px rgba(0,0,0,0.5)", fontSize: 13, lineHeight: 1.6 }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(viewDoc.content || "") }}
            />
          </div>
        </div>
      )}

      </>)}

      {pane === "conversations" && (<>
      {/* ── Conversations (existing jobDocs) ── */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ ...lbl, fontSize: 10, margin: 0 }}>Conversations {jobDocs.length > 0 && `(${jobDocs.length})`}</div>
          {!convoMode && !convoResult && (
            <button style={{ ...btnS, padding: "4px 10px", fontSize: 9 }} onClick={() => { setConvoMode(true); setConvoText(""); setConvoTitle(""); }}>
              + Add Conversation
            </button>
          )}
        </div>

        {/* Saved job docs list */}
        {jobDocs.map(doc => (
          <div key={doc.id} style={{ marginBottom: 6, border: `1px solid ${C.border}`, borderRadius: 5, overflow: "hidden" }}>
            <div className="jdf-convorow" style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", cursor: "pointer", background: expandedDoc === doc.id ? `${C.border}20` : "transparent" }}
              onClick={() => setExpandedDoc(expandedDoc === doc.id ? null : doc.id)}>
              <span style={{ fontSize: 11, color: C.muted, transform: expandedDoc === doc.id ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>▶</span>
              {renamingId === doc.id ? (
                <input
                  autoFocus
                  value={renameVal}
                  onClick={e => e.stopPropagation()}
                  onChange={e => setRenameVal(e.target.value)}
                  onBlur={() => {
                    const title = renameVal.trim();
                    if (title && title !== doc.title) upd("jobDocs", jobDocs.map(d => d.id === doc.id ? { ...d, title } : d));
                    setRenamingId(null);
                  }}
                  onKeyDown={e => {
                    if (e.key === "Enter") e.currentTarget.blur();
                    if (e.key === "Escape") setRenamingId(null);
                  }}
                  style={{ ...inp, flex: 1, fontSize: 11, padding: "3px 8px" }}
                />
              ) : (
                <span style={{ flex: 1, fontSize: 11, color: C.text }}>{doc.title || "Untitled"}</span>
              )}
              <span style={{ fontSize: 9, color: C.muted }}>{doc.date || ""}</span>
              {doc.participants?.length > 0 && <span style={{ fontSize: 9, color: C.muted }}>{doc.participants.join(", ")}</span>}
              {!convoMode && !convoResult && (
                <button style={{ ...btnS, padding: "2px 8px", fontSize: 9, opacity: convoExtracting ? 0.5 : 1 }} disabled={convoExtracting}
                  onClick={e => { e.stopPropagation(); runExtract(doc.content, { sourceDocId: doc.id, title: doc.title || "" }); }}>
                  {extractingDocId === doc.id ? "Extracting…" : "Extract"}
                </button>
              )}
              {editing && renamingId !== doc.id && (
                <button style={{ ...btnS, padding: "2px 6px", fontSize: 9 }} onClick={e => {
                  e.stopPropagation();
                  setRenameVal(doc.title || "");
                  setRenamingId(doc.id);
                }}>✎</button>
              )}
              {editing && <button style={{ ...btnD, padding: "2px 6px", fontSize: 9 }} onClick={e => {
                e.stopPropagation();
                setConfirmDeleteConvoId(doc.id);
              }}>×</button>}
            </div>
            {confirmDeleteConvoId === doc.id && (
              <div style={{ padding: "0 10px" }} onClick={e => e.stopPropagation()}>
                <ConfirmStrip
                  label={`Delete "${doc.title || "Untitled"}"?`}
                  onYes={() => { setConfirmDeleteConvoId(null); upd("jobDocs", jobDocs.filter(d => d.id !== doc.id)); }}
                  onNo={() => setConfirmDeleteConvoId(null)}
                />
              </div>
            )}
            {expandedDoc === doc.id && (
              <div style={{ padding: "8px 10px", borderTop: `1px solid ${C.border}`, maxHeight: 300, overflowY: "auto", whiteSpace: "pre-wrap", fontSize: 11, color: C.muted, lineHeight: 1.5, fontFamily: fl }}>
                {doc.content}
              </div>
            )}
          </div>
        ))}

        {/* Conversation input mode */}
        {convoMode && !convoResult && (
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 6, padding: 12, marginTop: 6 }}>
            <input placeholder="Title (e.g. Pre-Scan Call — Client Name)" value={convoTitle} onChange={e => setConvoTitle(e.target.value)}
              style={{ ...inp, marginBottom: 8, fontSize: 12 }} />
            <textarea placeholder="Paste conversation transcript here, or drag and drop a file..." value={convoText} onChange={e => setConvoText(e.target.value)}
              onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = C.blue; }}
              onDragLeave={e => { e.currentTarget.style.borderColor = C.border; }}
              onDrop={async e => {
                e.preventDefault(); e.currentTarget.style.borderColor = C.border;
                const file = e.dataTransfer.files?.[0];
                if (!file) return;
                const text = await file.text();
                setConvoText(text);
                if (!convoTitle) setConvoTitle(file.name.replace(/\.[^.]+$/, "").replace(/[_-]/g, " "));
              }}
              style={{ ...inp, minHeight: 120, resize: "vertical", fontSize: 11, lineHeight: 1.5, transition: "border-color 0.15s" }} />
            <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap", alignItems: "center" }}>
              <button style={{ ...btnS, padding: "5px 10px", fontSize: 10 }} onClick={() => {
                const fileInput = document.createElement("input");
                fileInput.type = "file";
                fileInput.accept = ".txt,.md,.srt,.vtt,.doc,.docx,.rtf,.json,.csv";
                fileInput.onchange = async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const text = await file.text();
                  setConvoText(text);
                  if (!convoTitle) setConvoTitle(file.name.replace(/\.[^.]+$/, "").replace(/[_-]/g, " "));
                };
                fileInput.click();
              }}>Upload File</button>
              <div style={{ flex: 1 }} />
              <button style={{ ...btnP, padding: "6px 14px", fontSize: 10, opacity: convoExtracting || !convoText.trim() ? 0.5 : 1 }}
                disabled={convoExtracting || !convoText.trim()}
                onClick={() => runExtract(convoText)}>
                {convoExtracting ? "Extracting…" : "Extract & Populate"}
              </button>
              <button style={{ ...btnS, padding: "6px 14px", fontSize: 10, opacity: !convoText.trim() ? 0.5 : 1 }}
                disabled={!convoText.trim()}
                onClick={() => {
                  if (!convoText.trim()) return;
                  const doc = { id: "jd_" + Date.now(), title: convoTitle || "Conversation", type: "conversation", content: convoText, date: new Date().toISOString().split("T")[0], participants: [], createdAt: new Date().toISOString() };
                  upd("jobDocs", [...jobDocs, doc]);
                  setConvoMode(false); setConvoText(""); setConvoTitle("");
                }}>
                Save Only
              </button>
              <button style={{ ...btnS, padding: "6px 14px", fontSize: 10, color: C.muted }}
                onClick={resetConvoFlow}>
                Cancel
              </button>
            </div>
            {convoError && <div style={{ fontSize: 10, color: C.danger, marginTop: 8 }}>{convoError}</div>}
          </div>
        )}

        {/* Extraction error from a saved-conversation Extract (input box not open) */}
        {convoError && !convoMode && !convoResult && (
          <div style={{ fontSize: 10, color: C.danger, marginTop: 6 }}>{convoError}</div>
        )}

        {/* ═══ Extraction review ═══ */}
        {convoResult && (() => {
          const r = convoResult;
          // Helper: get current job value as string for overwrite detection
          const curVal = (key) => {
            const v = j[key];
            if (v == null || v === "" || v === false) return "";
            if (Array.isArray(v)) return v.length > 0 ? (typeof v[0] === "string" ? v.join(", ") : `${v.length} items`) : "";
            if (typeof v === "boolean") return v ? "Yes" : "";
            return String(v);
          };
          // Fields to review — label, key, display value, existing value for overwrite warning
          const fields = [
            r.clientName && { key: "clientName", label: "Client", val: r.clientName },
            r.contactName && { key: "contactName", label: "Contact", val: r.contactName },
            r.project && { key: "project", label: "Project", val: r.project },
            r.description && { key: "description", label: "Description", val: r.description },
            r.industry && { key: "industry", label: "Industry", val: INDUSTRY_LABELS[r.industry] || r.industry },
            r.captureDate && { key: "captureDate", label: "Capture Date", val: r.captureDate },
            r.multiDay != null && { key: "multiDay", label: "Multi-Day", val: r.multiDay ? "Yes" : "No" },
            r.sessions?.length > 0 && { key: "sessions", label: "Sessions", val: r.sessions.map(s => `${s.date || "?"} (${s.dayType || "full"})`).join(", ") },
            r.startTime && { key: "startTime", label: "Start Time", val: r.startTime },
            r.scheduledTime && { key: "scheduledTime", label: "Call Time", val: r.scheduledTime },
            r.wrapTime && { key: "wrapTime", label: "Wrap Time", val: r.wrapTime },
            r.location && { key: "location", label: "Location", val: r.location },
            r.locationVenue && { key: "locationVenue", label: "Venue", val: r.locationVenue },
            r.locationCity && { key: "locationCity", label: "City", val: r.locationCity },
            r.locationState && { key: "locationState", label: "State", val: r.locationState },
            r.isStudio != null && { key: "isStudio", label: "Studio Job", val: r.isStudio ? "Yes" : "No" },
            r.crew && { key: "crew", label: "Crew", val: r.crew },
            r.selectedEquipment?.length > 0 && { key: "selectedEquipment", label: "Equipment", val: r.selectedEquipment.map(id => equipmentIdToName[id] || id).join(", ") },
            r.deliverableType && { key: "deliverableType", label: "Deliverable Type", val: r.deliverableType },
            r.deliveryDate && { key: "deliveryDate", label: "Delivery Date", val: r.deliveryDate },
            r.deliveryMethod && { key: "deliveryMethod", label: "Delivery Method", val: r.deliveryMethod },
            r.transport && { key: "transport", label: "Transport", val: r.transport },
            r.prepNotes && { key: "prepNotes", label: "Prep Notes", val: r.prepNotes },
            r.notes && { key: "notes", label: "Notes", val: r.notes },
            r.peoplePresent?.length > 0 && { key: "peoplePresent", label: "People On-Site", val: r.peoplePresent.join(", ") },
            r.clientContacts?.length > 0 && { key: "clientContacts", label: "Client Contacts", val: r.clientContacts.join(", ") },
          ].filter(Boolean).map(f => ({ ...f, existing: curVal(f.key) }));

          const toggleSkip = (key) => {
            const next = new Set(convoSkipped);
            next.has(key) ? next.delete(key) : next.add(key);
            setConvoSkipped(next);
          };

          const applyFields = () => {
            const updates = {};
            fields.forEach(f => {
              if (convoSkipped.has(f.key)) return;
              const val = r[f.key];
              if (val !== undefined && val !== null && val !== "") {
                if (f.key === "sessions" && Array.isArray(val)) {
                  updates.sessions = val.map(s => ({ ...BLANK_SESSION(), ...s }));
                } else if (f.key === "selectedEquipment" && Array.isArray(val)) {
                  // Convert equipment IDs to names (job stores names, AI returns IDs)
                  updates.selectedEquipment = val.map(id => equipmentIdToName[id] || id);
                } else {
                  updates[f.key] = val;
                }
              }
            });
            // Auto-fill studio address fields when isStudio is set (extracted values win)
            if (updates.isStudio === true) {
              const sp = studioPatch();
              for (const [k, v] of Object.entries(sp)) {
                if (updates[k] === undefined) updates[k] = v;
              }
            }
            // Link participants from Who's Who. Seed from legacy linkedEntities
            // (seededLinkedContacts) so the first linkedContacts write on an old
            // job doesn't shadow its legacy links; then merge, skipping dupes.
            const base = seededLinkedContacts(j);
            const existingContactIds = new Set(base.map(lc => lc.contactId));
            const newLinks = [];
            convoParticipants.forEach(p => {
              if (!p.contactId || p.isMe || existingContactIds.has(p.contactId)) return;
              existingContactIds.add(p.contactId); // prevent duplicates within batch
              newLinks.push({ contactId: p.contactId, role: p.role });
            });
            if (newLinks.length > 0) {
              updates.linkedContacts = [...base, ...newLinks];
            }
            // Save transcript as job doc in the same atomic update. An extract
            // run from an already-saved conversation updates that doc's
            // participants in place — never a duplicate row.
            const participantNames = convoParticipants.filter(p => p.search?.trim()).map(p => p.search.trim());
            if (convoSourceDocId) {
              updates.jobDocs = jobDocs.map(d => d.id === convoSourceDocId ? { ...d, participants: participantNames } : d);
            } else {
              const doc = { id: "jd_" + Date.now(), title: convoTitle || "Conversation", type: "conversation", content: convoText, date: new Date().toISOString().split("T")[0], participants: participantNames, createdAt: new Date().toISOString() };
              updates.jobDocs = [...jobDocs, doc];
            }
            updateJobFields(updates);
            resetConvoFlow();
          };

          const overwriteCount = fields.filter(f => f.existing && !convoSkipped.has(f.key)).length;

          // Who's Who: typeahead search helper
          const updateParticipant = (idx, patch) => {
            setConvoParticipants(prev => prev.map((p, i) => i === idx ? { ...p, ...patch } : p));
          };
          const searchContactsLocal = (query) => {
            if (!query || query.length < 2) return [];
            const q = query.toLowerCase();
            return (contacts || []).filter(c => c.name && c.name.toLowerCase().includes(q)).slice(0, 8);
          };

          return (
            <div style={{ border: `1px solid ${C.amber}30`, borderRadius: 6, padding: 12, marginTop: 6, background: `${C.amber}06` }}>

              {/* Who's Who — speaker identification */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 400, color: C.text }}>Who's Who — identify speakers</div>
                  <button style={{ ...btnS, padding: "3px 8px", fontSize: 9 }} onClick={() => {
                    setConvoParticipants(prev => [...prev, { label: null, snippet: "", name: "", search: "", contactId: null, role: "contact", isMe: false, showDropdown: false }]);
                  }}>+ Add Person</button>
                </div>
                {wwError && <div style={{ fontSize: 10, color: C.danger, marginBottom: 6 }}>{wwError}</div>}
                {convoParticipants.length === 0 && <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>No speaker labels detected. Add people manually or skip.</div>}
                <div style={{ display: "grid", gap: 6 }}>
                  {convoParticipants.map((p, pi) => (
                    <div key={pi} className="jdf-ww-row" style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "6px 8px", borderRadius: 4, background: p.isMe ? `${C.blue}08` : p.contactId ? `${C.green}08` : `${C.border}15` }}>
                      {/* Speaker label + snippet */}
                      <div style={{ minWidth: 70, flexShrink: 0 }}>
                        {p.label && <div style={{ fontSize: 9, color: C.muted, fontFamily: "'DM Mono', monospace" }}>{p.label}</div>}
                        {p.snippet && <div style={{ fontSize: 9, color: C.muted, fontStyle: "italic", marginTop: 2, lineHeight: 1.3 }}>"{p.snippet}"</div>}
                        {!p.label && <div style={{ fontSize: 9, color: C.muted }}>Manual</div>}
                      </div>
                      {/* Name / search input */}
                      <div className="jdf-ww-name" style={{ flex: 1, position: "relative" }}>
                        {p.isMe ? (
                          <div style={{ fontSize: 11, color: C.blue, padding: "4px 0" }}>{p.name} (you)</div>
                        ) : (
                          <>
                            <input
                              value={p.search}
                              placeholder="Who is this? Search contacts..."
                              onChange={e => updateParticipant(pi, { search: e.target.value, contactId: null, showDropdown: true })}
                              onFocus={() => updateParticipant(pi, { showDropdown: true })}
                              onBlur={() => setTimeout(() => updateParticipant(pi, { showDropdown: false }), 200)}
                              style={{ ...inp, fontSize: 11, padding: "4px 8px", margin: 0 }}
                            />
                            {p.contactId && (() => {
                              const ent = (contacts || []).find(c => c.id === p.contactId);
                              return ent ? <div style={{ fontSize: 9, color: C.green, marginTop: 2 }}>Linked: {ent.name}{ent.company ? ` (${ent.company})` : ""}</div> : null;
                            })()}
                            {/* Typeahead dropdown */}
                            {p.showDropdown && p.search.length >= 2 && (() => {
                              const results = searchContactsLocal(p.search);
                              const showCreate = p.search.trim().length >= 2 && !p.contactId && !results.find(e => e.name.toLowerCase().trim() === p.search.toLowerCase().trim());
                              return (results.length > 0 || showCreate) ? (
                                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 10, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 4, marginTop: 2, maxHeight: 160, overflowY: "auto" }}>
                                  {results.map(ent => (
                                    <div key={ent.id}
                                      onMouseDown={(e) => { e.preventDefault(); updateParticipant(pi, { contactId: ent.id, search: ent.name, showDropdown: false }); }}
                                      style={{ padding: "6px 10px", cursor: "pointer", fontSize: 11, display: "flex", justifyContent: "space-between" }}
                                      onMouseEnter={e => e.currentTarget.style.background = `${C.border}20`}
                                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                                      <span style={{ color: C.text }}>{ent.name}</span>
                                      <span style={{ color: C.muted, fontSize: 9 }}>{ent.company || ""}</span>
                                    </div>
                                  ))}
                                  {showCreate && (
                                    <div
                                      onMouseDown={async (e) => {
                                        e.preventDefault();
                                        if (p.contactId) return;
                                        setWwError(null);
                                        const trimmed = p.search.trim();
                                        const existing = (contacts || []).find(ct => ct.name && ct.name.toLowerCase().trim() === trimmed.toLowerCase());
                                        if (existing) { updateParticipant(pi, { contactId: existing.id, search: existing.name, showDropdown: false }); return; }
                                        const newEnt = BLANK_CONTACT();
                                        newEnt.name = trimmed;
                                        try {
                                          await saveContact(newEnt);
                                          updateParticipant(pi, { contactId: newEnt.id, showDropdown: false });
                                        } catch (err) { setWwError(err.message || "Could not create contact."); }
                                      }}
                                      style={{ padding: "6px 10px", cursor: "pointer", fontSize: 11, color: C.green, borderTop: results.length > 0 ? `1px solid ${C.border}` : "none", display: "flex", alignItems: "center", gap: 4 }}
                                      onMouseEnter={e => e.currentTarget.style.background = `${C.green}10`}
                                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                                      + Create "{p.search.trim()}"
                                    </div>
                                  )}
                                </div>
                              ) : null;
                            })()}
                          </>
                        )}
                      </div>
                      {/* Role dropdown */}
                      {!p.isMe && (
                        <select value={p.role} onChange={e => updateParticipant(pi, { role: e.target.value })}
                          style={{ ...sel, width: "auto", padding: "3px 20px 3px 6px", fontSize: 10 }}>
                          {Object.entries(JOB_ROLES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                      )}
                      {/* Unlink button */}
                      {!p.isMe && p.contactId && (
                        <button style={{ ...btnD, padding: "2px 6px", fontSize: 9 }}
                          onClick={() => updateParticipant(pi, { contactId: null, search: "" })}>×</button>
                      )}
                      {/* Dismiss row */}
                      {!p.isMe && (
                        <button style={{ ...btnS, padding: "2px 6px", fontSize: 9, color: C.muted }}
                          onClick={() => setConvoParticipants(prev => prev.filter((_, i) => i !== pi))}>dismiss</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {convoParticipants.length > 0 && fields.length > 0 && <div style={{ height: 1, background: C.border, margin: "10px 0" }} />}

              <div style={{ fontSize: 12, fontWeight: 400, color: C.text, marginBottom: 4 }}>{fields.length > 0 ? "Extracted Fields — uncheck to skip" : "No fields extracted"}</div>
              {fields.length === 0 && <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>Could not extract structured data. The transcript will still be saved as a job doc.</div>}
              {overwriteCount > 0 && <div style={{ fontSize: 10, color: C.amber, marginBottom: 8 }}>{overwriteCount} field{overwriteCount !== 1 ? "s" : ""} will overwrite existing data</div>}
              <div style={{ display: "grid", gap: 4 }}>
                {fields.map(f => (
                  <label key={f.key} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "4px 6px", borderRadius: 4, cursor: "pointer", background: convoSkipped.has(f.key) ? "transparent" : f.existing ? `${C.amber}08` : `${C.green}08` }}>
                    <input type="checkbox" checked={!convoSkipped.has(f.key)} onChange={() => toggleSkip(f.key)} style={{ accentColor: f.existing ? C.amber : C.green, marginTop: 2 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 9, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>{f.label}{f.existing ? " ⟵ overwrite" : ""}</div>
                      <div style={{ fontSize: 11, color: convoSkipped.has(f.key) ? C.muted : C.text, textDecoration: convoSkipped.has(f.key) ? "line-through" : "none" }}>{f.val}</div>
                      {f.existing && !convoSkipped.has(f.key) && <div style={{ fontSize: 10, color: C.muted, marginTop: 1 }}>Current: {f.existing}</div>}
                    </div>
                  </label>
                ))}
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 10, alignItems: "center" }}>
                {editing ? (
                  <button style={{ ...btnP, padding: "6px 14px", fontSize: 10 }} onClick={applyFields}>
                    Apply {fields.length - convoSkipped.size} Fields
                  </button>
                ) : (
                  <div style={{ fontSize: 10, color: C.amber }}>Toggle Edit mode to apply fields</div>
                )}
                {!convoSourceDocId && (
                  <button style={{ ...btnS, padding: "6px 14px", fontSize: 10, color: C.muted }} onClick={() => { setConvoResult(null); }}>
                    Back to Edit
                  </button>
                )}
                <button style={{ ...btnS, padding: "6px 14px", fontSize: 10, color: C.muted }} onClick={resetConvoFlow}>
                  Cancel
                </button>
              </div>
            </div>
          );
        })()}
      </div>

      </>)}

      {pane === "files" && (<>
      {/* ── Files ── */}
      {/* Drop zone / upload */}
      <div
        onDragOver={e => { e.preventDefault(); setFilesDragOver(true); }}
        onDragLeave={() => setFilesDragOver(false)}
        onDrop={async e => { e.preventDefault(); setFilesDragOver(false); await handleUpload(Array.from(e.dataTransfer.files)); }}
        style={{
          border: `1.5px dashed ${filesDragOver ? C.blue : C.border}`,
          borderRadius: 6,
          padding: "10px 14px",
          marginBottom: 10,
          background: filesDragOver ? `${C.blue}08` : "transparent",
          textAlign: "center",
          cursor: "pointer",
          transition: "all 0.15s",
        }}
        onClick={() => { const fileInp = document.createElement("input"); fileInp.type = "file"; fileInp.multiple = true; fileInp.onchange = async e => { await handleUpload(Array.from(e.target.files)); }; fileInp.click(); }}
      >
        <span style={{ fontSize: 11, color: filesUploading ? C.muted : C.text }}>
          {filesUploading ? "Uploading…" : filesDragOver ? "Drop to upload" : "Drop files here or click to browse"}
        </span>
      </div>
      {filesError && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", marginBottom: 8, background: `${C.danger}08`, border: `1px solid ${C.danger}30`, borderRadius: 6 }}>
          <span style={{ fontSize: 11, color: C.danger, flex: 1 }}>{filesError}</span>
          <button style={{ ...btnS, padding: "3px 8px", fontSize: 9 }} onClick={() => setFilesError(null)}>Dismiss</button>
        </div>
      )}
      <div style={{ ...lbl, marginBottom: 6, fontSize: 10 }}>Files {files.length > 0 && `(${files.length})`}</div>
      {files.length > 0 ? files.map((f, i) => (
        <FileRow
          key={i}
          file={f}
          editing={editing}
          onToggleCrew={async () => {
            setFilesError(null);
            const prev = files;
            const updated = files.map((ff, idx) => idx === i ? { ...ff, crewVisible: !ff.crewVisible } : ff);
            setJobFiles(updated);
            try { await dataService.files.save(j.id, updated); } catch (e) { console.error(e); setJobFiles(prev); setFilesError("Could not save the crew-visible change: " + (e.message || "unknown")); }
          }}
          onDelete={async () => {
            setFilesError(null);
            const prev = files;
            const updated = files.filter((_, idx) => idx !== i); setJobFiles(updated);
            try { await dataService.files.save(j.id, updated); } catch (e) { console.error(e); setJobFiles(prev); setFilesError("Could not delete the file: " + (e.message || "unknown")); }
          }}
        />
      )) : <div style={{ fontSize: 11, color: C.muted }}>No files attached</div>}
      </>)}
    </div>
  );
}
